import {
  Box,
  Collapse,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SparkDropdownItem from 'components/Dashboard/SparkDropdownItem';
import { useDialog } from 'hooks/common/useDialog';
import type { JoinedSparks } from 'types/Spark/JoinedSpark';
import React from 'react';
import { Link } from 'react-router-dom';
import { SPARK_ROUTES } from 'constants/routes';
import { useSparkSearch } from 'hooks/spark';
import SparkSearchResultSkeleton from 'sharedComponents/Skeleton/SparkSearchResultSkeleton';

type SparkSearchDropdownProps = {
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  onSparkClick: (spark: JoinedSparks) => void;
  shouldRenderOrSuggestion?: boolean;
  placeholder?: string;
};

function SparkSearchDropdown({
  endAdornment,
  startAdornment,
  onSparkClick,
  shouldRenderOrSuggestion = true,
  placeholder = '',
}: SparkSearchDropdownProps) {
  const [localQuery, setLocalQuery] = React.useState('');

  const { data: joinedSparks = [], isLoading: isSearchingSparks } =
    useSparkSearch(
      {
        filters: { search: localQuery },
      },
      { enabled: localQuery.length >= 3 }
    );

  const [isFocused, setIsFocused] = React.useState<boolean>(false);
  const { open, openDialog, closeDialog } = useDialog();
  const textFieldRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!isFocused || localQuery.length < 3) {
      closeDialog();
    } else {
      openDialog();
    }
  }, [closeDialog, isFocused, openDialog, localQuery]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const query = e.target.value;
    setLocalQuery(query);
  };

  return (
    <Box
      sx={{ position: 'relative', width: '100%' }}
      component='div'
      ref={textFieldRef}
    >
      <TextField
        placeholder={placeholder}
        fullWidth
        value={localQuery}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
        }}
        sx={{
          backgroundColor: '#ffffff',
        }}
        InputProps={{
          endAdornment,
          startAdornment,
        }}
      />

      <Collapse
        in={open}
        sx={{ position: 'absolute', top: '3.8rem', zIndex: 2 }}
        unmountOnExit={true}
      >
        <Paper
          elevation={1}
          sx={{
            maxHeight: '700px',
            overflowY: 'auto',
          }}
        >
          <Grid
            container
            bgcolor={(theme) => theme.palette.common.white}
            borderRadius='10px'
            width={textFieldRef.current?.offsetWidth}
          >
            {isSearchingSparks && <SparkSearchResultSkeleton />}
            {!isSearchingSparks && !joinedSparks.length && (
              <Grid container justifyContent='center' padding={2}>
                <Typography
                  variant='body1'
                  color={(theme) => theme.palette.labelColor}
                >
                  No sparks found
                </Typography>
              </Grid>
            )}
            {!isSearchingSparks &&
              joinedSparks.length > 0 &&
              joinedSparks.map((spark, index) => (
                <React.Fragment key={spark.id}>
                  {index !== 0 && (
                    <Box width='100%'>
                      <Divider />
                    </Box>
                  )}
                  <SparkDropdownItem
                    spark={spark}
                    onClick={() => {
                      setLocalQuery('');
                      onSparkClick(spark);
                    }}
                  />
                </React.Fragment>
              ))}

            {shouldRenderOrSuggestion && (
              <>
                <Grid
                  item
                  container
                  xs={12}
                  alignItems='center'
                  justifyContent='center'
                  flexWrap='nowrap'
                >
                  <Box width='50%'>
                    <Divider />
                  </Box>
                  <Typography sx={{ padding: '0 5px' }}>OR</Typography>
                  <Box width='50%'>
                    <Divider />
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  container
                  justifyContent='center'
                  alignItems='center'
                >
                  <Link
                    style={{ cursor: 'pointer', color: 'black' }}
                    to={SPARK_ROUTES.SPARK_BUILDER}
                  >
                    Create a Spark
                  </Link>
                  <Typography marginLeft={1}> to "{localQuery}"</Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </Collapse>
    </Box>
  );
}

export default SparkSearchDropdown;
